import { useEffect, useState } from "react";
import { formatDate } from "../lib/format";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { newsApi, type ArticleSummary } from "../api/news";

/**
 * Dashboard widget. Renders the top N visible articles for
 * the caller's roles (filtered server-side via visible_to_roles).
 */
export function LatestNewsWidget({ limit = 3 }: { limit?: number }) {
  const { t, i18n } = useTranslation();
  const [items, setItems] = useState<ArticleSummary[] | null>(null);

  useEffect(() => {
    void newsApi
      .latest(limit, i18n.resolvedLanguage ?? "en")
      .then(setItems)
      .catch(() => setItems([]));
  }, [limit, i18n.resolvedLanguage]);

  if (items === null) return null;
  if (items.length === 0) {
    return (
      <section style={card}>
        <header style={head}>
          <strong>{t("news.latest", "Latest news")}</strong>
          <Link to="/news" className="widget-more-link" style={moreLink}>{t("news.viewAll", "View all →")}</Link>
        </header>
        <p style={{ color: "var(--ink-4)", fontSize: 13, margin: 0 }}>{t("news.empty", "No news yet.")}</p>
      </section>
    );
  }

  return (
    <section style={card}>
      <header style={head}>
        <strong>{t("news.latest", "Latest news")}</strong>
        <Link to="/news" className="widget-more-link" style={moreLink}>{t("news.viewAll", "View all →")}</Link>
      </header>
      <ul style={list}>
        {items.map((a) => (
          <li key={a.id}>
            <Link to={`/news/${a.slug}`} style={itemLink}>
              <strong style={{ fontSize: 14, color: "var(--brand-ink)" }}>{a.title}</strong>
              {a.excerpt && (
                <span style={{ color: "var(--ink-3)", fontSize: 12, display: "block", marginTop: 2 }}>
                  {a.excerpt.length > 120 ? a.excerpt.slice(0, 120) + "…" : a.excerpt}
                </span>
              )}
              {a.publishedAt && (
                <small style={{ color: "var(--ink-4)", display: "block", marginTop: 4 }}>
                  {formatDate(a.publishedAt)}
                </small>
              )}
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

const card: React.CSSProperties = {
  background: "var(--surface)", padding: 16, borderRadius: 10, boxShadow: "var(--shadow-1)",
  display: "flex", flexDirection: "column", gap: 12,
};
const head: React.CSSProperties = { display: "flex", justifyContent: "space-between", alignItems: "center", color: "var(--ink)", fontSize: 14 };
const list: React.CSSProperties = { listStyle: "none", padding: 0, margin: 0, display: "flex", flexDirection: "column", gap: 12 };
const itemLink: React.CSSProperties = { textDecoration: "none", display: "block", padding: 8, borderRadius: 6, transition: "background 120ms" };
const moreLink: React.CSSProperties = { color: "var(--brand-ink)", fontSize: 12, textDecoration: "none" };
